import { css, ThemeProvider as EmotionThemeProvider } from '@emotion/react'
import React, { memo } from 'react'
import { ParallaxProvider } from 'react-scroll-parallax'

import { MediaContextProvider, mediaStyle } from './media'

export type Theme = typeof theme

export const theme = {
  colors: {
    variants: {
      neutralLight2: '#FFFFFF',
      neutralLight1: '#F8F7F5',
      neutralLight0: '#000000',
      neutralDark3: '#797979',
      neutralDark2: '#484C3A',
      neutralDark1: '#0D0D0D',
      primaryDark: '#A0A37B',
      primaryLight: '#A0A37B',
      danger: '#D51000',
    },
  },
  fontFamily: {
    heading: "'futura-pt', sans-serif",
    paragraph: "'halyard-display', sans-serif",
  },
}

export type Styles = typeof styles

export const styles = {
  label: css`
    color: ${theme.colors.variants.primaryLight};
    font-family: ${theme.fontFamily.paragraph};
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.125rem;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  `,
  button: css`
    color: ${theme.colors.variants.neutralLight2};
    font-family: ${theme.fontFamily.paragraph};
    font-size: 0.875rem;
    line-height: 1.25rem;
    letter-spacing: 0.05em;
    font-weight: 400;
  `,
  title: {
    xl: css`
      color: ${theme.colors.variants.neutralLight2};
      font-family: ${theme.fontFamily.heading};
      font-size: 3.75rem;
      line-height: 4rem;
      font-weight: 500;
      letter-spacing: 0.01em;
      text-transform: uppercase;

      @media (max-width: 1199px) {
        font-size: 2.625rem;
        font-weight: 600;
        line-height: 3.125rem;
      }
    `,
    large: css`
      color: ${theme.colors.variants.neutralDark1};
      font-family: ${theme.fontFamily.heading};
      font-size: 3.75rem;
      line-height: 4.375rem;
      font-weight: 300;
      letter-spacing: 0.01em;
      text-transform: uppercase;

      @media (max-width: 1199px) {
        font-size: 2.25rem;
        line-height: 2.875rem;
      }
    `,
    medium: css`
      color: ${theme.colors.variants.neutralDark1};
      font-family: ${theme.fontFamily.heading};
      font-size: 2.5rem;
      line-height: 2.9375rem;
      font-weight: 300;
      letter-spacing: 0.01em;
      text-transform: uppercase;

      @media (max-width: 1199px) {
        font-size: 1.75rem;
        line-height: 2.375rem;
        text-transform: none;
      }
    `,
    small: css`
      color: ${theme.colors.variants.neutralLight2};
      font-family: ${theme.fontFamily.heading};
      font-size: 2rem;
      line-height: 2.375rem;
      font-weight: 500;
      letter-spacing: 0.01em;

      @media (max-width: 1199px) {
        font-size: 1.625rem;
        line-height: 1.875rem;
      }
    `,
    xs: css`
      color: ${theme.colors.variants.neutralDark1};
      font-family: ${theme.fontFamily.heading};
      font-size: 1.5rem;
      line-height: 1.75rem;
      font-weight: 300;
      letter-spacing: 0.01em;
    `,
  },
  description: css`
    color: ${theme.colors.variants.neutralDark1};
    font-family: ${theme.fontFamily.paragraph};
    font-size: 1.125rem;
    line-height: 1.625rem;
    font-weight: 200;
    letter-spacing: 0.01em;
  `,
  img: css`
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
    will-change: contents;
  `,
}

interface Props {
  children: any
}

export const ThemeProvider = memo(function ThemeProvider({ children }: Props) {
  return (
    <EmotionThemeProvider theme={theme}>
      <style type="text/css">{mediaStyle}</style>
      <MediaContextProvider>
        <ParallaxProvider>{children}</ParallaxProvider>
      </MediaContextProvider>
    </EmotionThemeProvider>
  )
})
